import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PageLoader() {
  const [isLoading, setIsLoading] = useState(true);
  const [showCurtains, setShowCurtains] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate loading progress
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 100);

    // Trigger curtain animation
    const curtainTimer = setTimeout(() => {
      setShowCurtains(true);
    }, 1800);

    // Hide loader after curtain animation
    const hideTimer = setTimeout(() => {
      setIsLoading(false);
    }, 2800);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(curtainTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  if (!isLoading) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9999] overflow-hidden"
      >
        {/* Vertical Curtain Rectangles */}
        <div className="absolute inset-0 flex">
          {[...Array(5)].map((_, index) => (
            <motion.div
              key={index}
              initial={{ scaleY: 1 }}
              animate={{
                scaleY: showCurtains ? 0 : 1,
              }}
              transition={{
                duration: 0.8,
                delay: showCurtains ? index * 0.08 : 0,
                ease: [0.76, 0, 0.24, 1],
              }}
              className="flex-1 origin-top"
              style={{
                background: `linear-gradient(180deg, 
                  ${index % 2 === 0 ? '#0A1F44' : '#071733'} 0%, 
                  ${index % 2 === 0 ? '#071733' : '#0A1F44'} 100%)`,
              }}
            />
          ))}
        </div>

        {/* Main Content Layer */}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: showCurtains ? 0 : 1 }}
          transition={{ duration: 0.4 }}
          className="absolute inset-0 bg-gradient-to-br from-primary-navy via-primary-navy-dark to-primary-navy flex items-center justify-center"
        >
          {/* Animated Background Shapes */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="absolute top-20 left-20 w-64 h-64 bg-accent-blue/10 rounded-full blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1.2, 1, 1.2],
                rotate: [90, 0, 90],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear',
              }}
              className="absolute bottom-20 right-20 w-80 h-80 bg-accent-cyan/10 rounded-full blur-3xl"
            />
          </div>

          {/* Main Content */}
          <div className="relative z-10 flex flex-col items-center gap-8">
            {/* ZORAXO Logo Animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                ease: [0.6, 0.05, 0.01, 0.9],
              }}
              className="flex flex-col items-center gap-2"
            >
              <motion.h1
                className="text-6xl md:text-8xl font-bold text-white tracking-tight uppercase"
                initial={{ letterSpacing: '0.5em', opacity: 0 }}
                animate={{ letterSpacing: '0.05em', opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                ZORAXO
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="text-accent-cyan text-sm md:text-base tracking-widest uppercase"
              >
                Technologies Pvt Ltd
              </motion.p>
            </motion.div>

            {/* Loading Progress Bar */}
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: '300px' }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="relative h-1 bg-white/10 rounded-full overflow-hidden"
            >
              <motion.div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent-blue to-accent-cyan rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 0.2 }}
              />
            </motion.div>

            {/* Loading Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="flex items-center gap-2"
            >
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-4 h-4 border-2 border-accent-blue border-t-transparent rounded-full"
              />
              <span className="text-white/60 text-sm uppercase tracking-wider">
                Loading Experience...
              </span>
            </motion.div>
          </div>

          {/* Bottom Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="absolute bottom-12 text-center"
          >
            <p className="text-white/40 text-xs md:text-sm tracking-wider uppercase">
              Transforming Enterprises with Innovation
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
